/**
 * ClientInvite — Generate a shareable invite link/code for clients.
 */

import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, Share } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { generateCoachInviteLink } from '@/lib/trainers';
import { X, Copy, Check } from 'lucide-react-native';

interface Props {
  onClose: () => void;
}

export default function ClientInvite({ onClose }: Props) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    generateInvite();
  }, []);

  const generateInvite = async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { error } = await supabase.from('coach_invites').insert({
        trainer_profile_id: profile.id,
        code,
        expires_at: expiresAt.toISOString(),
      });

      if (error) {
        // Table may not exist yet — show code anyway for UI demo
        console.warn('coach_invites insert error:', error.message);
      }
      setInviteCode(code);
    } catch {
      setInviteCode('ERROR');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteCode) return;
    const link = `duel://invite/${inviteCode}`;
    try {
      await Clipboard.setStringAsync(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      Alert.alert('Oops', "Couldn't copy to clipboard. Try again.");
    }
  };

  const link = inviteCode ? `duel://invite/${inviteCode}` : '';
  const coachHandle = profile?.handle ?? profile?.id ?? '';
  const inviteLink = coachHandle ? generateCoachInviteLink(coachHandle) : link;

  return (
    <View style={[styles.overlay]}>
      <View style={[styles.modal, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: c.text }]}>Invite Client</Text>
          <TouchableOpacity onPress={onClose}>
            <X size={20} color={c.textSecondary} />
          </TouchableOpacity>
        </View>

        {loading ? (
          <Text style={[styles.hint, { color: c.textSecondary }]}>Generating invite...</Text>
        ) : (
          <>
            <View style={[styles.codeBox, { backgroundColor: c.background, borderColor: c.border }]}>
              <Text style={[styles.code, { color: c.text }]}>{inviteCode}</Text>
            </View>

            <Text style={[styles.hint, { color: c.textSecondary }]}>
              Share this link with your client. Expires in 7 days.
            </Text>

            <View style={[styles.linkRow, { backgroundColor: c.background, borderColor: c.border }]}>
              <Text style={[styles.linkText, { color: c.textSecondary }]} numberOfLines={1}>
                {link}
              </Text>
              <TouchableOpacity onPress={handleCopy} style={styles.copyBtn}>
                {copied ? (
                  <Check size={18} color={c.accent} />
                ) : (
                  <Copy size={18} color={c.accent} />
                )}
              </TouchableOpacity>
            </View>

            {/* Share invite link */}
            <View style={{ alignItems: 'center', gap: 12 }}>
              <Text style={{ color: c.textSecondary, fontSize: 13, textAlign: 'center' }}>
                Share your coaching profile link:
              </Text>
              <Text style={{ color: c.accent, fontSize: 13, fontWeight: '600', textAlign: 'center' }} numberOfLines={2}>
                {inviteLink}
              </Text>
              <TouchableOpacity
                onPress={() => Share.share({ message: `Join me on DUEL! ${inviteLink}`, url: inviteLink })}
                style={{ backgroundColor: c.accent, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 4 }}>
                <Text style={{ color: c.accentForeground, fontWeight: '700' }}>Share Invite Link</Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  modal: {
    width: '85%',
    borderWidth: 1,
    borderRadius: 2,
    padding: 20,
    gap: 14,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '800',
  },
  codeBox: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    alignItems: 'center',
  },
  code: {
    fontSize: 28,
    fontWeight: '900',
    letterSpacing: 4,
  },
  hint: {
    fontSize: 13,
    textAlign: 'center',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
    padding: 10,
    gap: 8,
  },
  linkText: {
    flex: 1,
    fontSize: 13,
  },
  copyBtn: {
    padding: 4,
  },

});
